import HeaderPreLogin from '@/components/common/HeaderPreLogin';
import { HeaderPreLoginProps } from '@/components/common/HeaderPreLogin/HeaderPreLogin.types';
import MaintenanceTile from '@/components/common/MaintenanceTile';
import NotificationTileApl from '@/components/common/NotificationTileApl';
import ProductWidget from '@/components/common/ProductWidget';
import {
    CMS_LANGUAGE,
    MARKET_NAMES,
    PRODUCT_WIDGET_ID,
    STORAGE_KEYS
} from '@/constants/const';
import { ROUTES_URL } from '@/constants/routesConst';
import { useShippingState } from '@/hooks/reduxUtilHook';
import useAuth from '@/hooks/useAuth';
import useKrCmsContentIds from '@/hooks/useKrCmsContentIds';
import { getMaintenanceBanner, getWhatNewProductWidget } from '@/services/announcementService';
import { updateShipToAddressSelected } from '@/store/actions/shippingactions';
import { getCustomerState } from '@/store/index';
import { GetConfigReturn } from '@/types/config';
import { ShipToAddressSelected } from '@/types/store/shipping';
import { getExpredLicenseCount } from '@/utils/country-wise-utils/commonUtils_IDN';
import { getFilterBanners, filterWidgetsByPage, getProductWidgetPageTitle } from '@/utils/maintenanceBannersUtil';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useMaintenance } from 'providers/MaintenanceProvider';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';

import { logger } from '@/utils/logger';
const Footer = dynamic(() => import('@/components/common/Footer'));
const ShiptoCodePopUp = dynamic(() => import('@/components/ShiptoCodePopUp'), { ssr: false });
const Header = dynamic(() => import('@/components/common/Header'), { ssr: false });

export type LayoutProps = {
    children?: ReactNode;
    isAdminConsole?: boolean;
    shouldExpandMaxHeight?: boolean;
    contentClassName?: string
} & Pick<HeaderPreLoginProps, 'onLanguageChange' | 'oktaAuth'>;

const Layout: FC<LayoutProps> = ({ children, contentClassName = '', ...restProps }) => {
    const { isAdminConsole = false, shouldExpandMaxHeight = false } = restProps;
    const auth = useAuth();
    const router = useRouter();
    const [customerCode, setCustomerCode] = useState<string | null>(null);
    const dispatch = useDispatch();
    const { shipToAddressListPaginator } = useShippingState();
    const { publicRuntimeConfig } = getConfig() as GetConfigReturn;
    const { ref, inView } = useInView({ triggerOnce: true, rootMargin: '0px 0px 100px 0px' });
    const krCmsContentIds = useKrCmsContentIds();

    const isIDMarket = publicRuntimeConfig.COUNTRY_CODE === MARKET_NAMES.ID;
    const isVNMarket = publicRuntimeConfig.COUNTRY_CODE === MARKET_NAMES.VN;
    const preAcceptedTnC = router.asPath === ROUTES_URL.TERMS_AND_PRIVACY;

    //** APL license expiry notification *//
    const customerInfo = useSelector((state) => getCustomerState(state)?.customerInfoArr);
    const [accBlocked, setAccBlocked] = useState(false);
    const [licenseExpCount, setLicenseExpCount] = useState(0);
    const [hiddenTile, setHiddenTile] = useState(true);
    const [maintenanceBanners, setMaintenanceBanners] = useState<Record<string, any>[]>([]);
    const [productWidgets, setProductWidgets] = useState<Record<string, any>[]>([]);
    const { checkMaintenanceStatus } = useMaintenance();
    useEffect(() => {
        const custCode = localStorage.getItem(STORAGE_KEYS.CUSTOMER_CODE);
        if (custCode && custCode === 'all') {
            setHiddenTile(false);
            return;
        }
        if (customerInfo?.shipTo) {
            const accBlockedTxt = customerInfo?.shipTo[0]?.customerBlock;
            const count = getExpredLicenseCount(customerInfo?.shipTo[0]?.licenses ?? []);

            const isClearBtnClicked = localStorage.getItem(STORAGE_KEYS.SHOW_LICENSE_TILE);

            if (isClearBtnClicked === 'true' || isClearBtnClicked === null) {
                if (accBlockedTxt === 'blocked') {
                    setHiddenTile(true);
                    setAccBlocked(true);
                } else {
                    setHiddenTile(false);
                    setAccBlocked(false);
                }

                if (count > 0) {
                    setHiddenTile(true);
                    setLicenseExpCount(count);
                } else {
                    setHiddenTile(false);
                    setLicenseExpCount(0);
                }
            } else {
                setAccBlocked(false);
                setLicenseExpCount(0);
            }
        }
    }, [customerInfo, hiddenTile]);

    const handleClose = () => {
        localStorage.setItem(STORAGE_KEYS.SHOW_LICENSE_TILE, 'false');
        setHiddenTile(false);
    };

    //** APL license expiry notification end *//

    const updateShipToAdd = (item: ShipToAddressSelected) => {
        if (item.customerCode) {
            localStorage.removeItem(STORAGE_KEYS.CUSTOMER_CODE);
            localStorage.setItem(STORAGE_KEYS.CUSTOMER_CODE, item.customerCode);
        }
        localStorage.removeItem(STORAGE_KEYS.SHIP_TO_CODE);
        localStorage.setItem(STORAGE_KEYS.SHIP_TO_CODE, item?.shipToCustomerCode!);
        if (shipToAddressListPaginator?.salesOrgSelectedState?.value) {
            localStorage.setItem(STORAGE_KEYS.SALES_ORGANIZATION, shipToAddressListPaginator.salesOrgSelectedState.value);
        }
        updateShipToAddressSelected({
            customerCode: item.customerCode ? item.customerCode : customerCode,
            customerShipToCode: item,
            dispatch,
            salesOrg: shipToAddressListPaginator?.salesOrgSelectedState
        });
    };

    const fetchMaintenanceBanner = async () => {
        try {
            const country = publicRuntimeConfig.COUNTRY_CODE ?? '';
            const variables = {
                itemId: krCmsContentIds.maintenanceBannerItemId,
                lang: CMS_LANGUAGE[country]
            };
            const response = await getMaintenanceBanner(variables);
            if (response?.maintenanceBanners && response?.maxNumberOfItem) {
                const banners = Array.isArray(response.maintenanceBanners)
                    ? response.maintenanceBanners
                    : response.maintenanceBanners.value || [];
                setMaintenanceBanners(banners.slice(0, response.maxNumberOfItem));
                checkMaintenanceStatus(banners);
            }
        } catch (error) {
            logger.error(error);
        }
    };

    const fetchProductWidget = async () => {
        try {
            const variables = {
                template: '{B7EA7586-974F-4F3D-BECE-198A79621BF9}',
                path: PRODUCT_WIDGET_ID[publicRuntimeConfig.COUNTRY_CODE!],
                pageSize: 1000,
                lang: CMS_LANGUAGE[publicRuntimeConfig.COUNTRY_CODE!],
                after: ''
            };
            const response = await getWhatNewProductWidget(variables);
            if (response?.results) {
                setProductWidgets(response.results);
            }
        } catch (error) {
            logger.error(error);
        }
    };

    useEffect(() => {
        if (
            !localStorage.getItem(STORAGE_KEYS.SHIP_TO_CODE) && // use localStorage here instead of Redux now to avoid stale value
            (router.asPath?.includes(ROUTES_URL.PRODUCT_DETAILS) ||
                router.asPath?.includes(ROUTES_URL.PRODUCT_LISTING) ||
                router.asPath?.includes(ROUTES_URL.CART) ||
                router.asPath?.includes(ROUTES_URL.CHECKOUT))
        ) {
            router.push(ROUTES_URL.HOME);
        }
        setCustomerCode(localStorage.getItem(STORAGE_KEYS.CUSTOMER_CODE));
        fetchProductWidget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!krCmsContentIds.isReady) return;
        localStorage.setItem(STORAGE_KEYS.CLOSE_MAINTENANCE_BANNER, JSON.stringify([]));
        fetchMaintenanceBanner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [krCmsContentIds.isReady, krCmsContentIds.maintenanceBannerItemId]);

    const filteredBanners = useMemo(
        () =>
            getFilterBanners({
                asPath: router?.asPath,
                authOk: auth?.ok,
                maintenanceBanners
            }),
        [auth?.ok, maintenanceBanners, router?.asPath]
    );

    const productWidgetPageTitle = useMemo(
        () => getProductWidgetPageTitle({ asPath: router?.asPath }),
        [router?.asPath]
    );

    return (
        <div className={shouldExpandMaxHeight ? 'min-h-screen flex flex-col' : ''}>
            {filteredBanners ? <MaintenanceTile maintenanceBanners={filteredBanners} /> : null}
            {isIDMarket && licenseExpCount > 0 && hiddenTile && !isAdminConsole ? (
                <NotificationTileApl licenseExpCount={licenseExpCount} handleClose={handleClose} />
            ) : null}
            {!isAdminConsole &&
            // Fix: Check if we're on a legal page AND user is not authenticated
            (router.asPath === ROUTES_URL.TERMS_AND_CONDITIONS ||
                router.asPath === ROUTES_URL.PRIVACY ||
                router.asPath === ROUTES_URL.ABOUT_US ||
                router.asPath === ROUTES_URL.AUP ||
                router.asPath?.includes(ROUTES_URL.CONTACT_US) ||
                router.asPath?.includes(ROUTES_URL.SOCIAL_ORG) ||
                router.asPath?.includes(ROUTES_URL.NEW_REGISTRATION) ||
                router.asPath?.includes(ROUTES_URL.NEW_REGISTER_CUSTOMER) ||
                (isVNMarket && router.asPath?.includes('/announcement/article-detail'))) &&
            !auth.ok ? (
                <HeaderPreLogin onLanguageChange={restProps?.onLanguageChange} oktaAuth={restProps.oktaAuth} />
            ) : !isAdminConsole ? (
                <Header
                    updateShipToAdd={updateShipToAdd}
                    isSearchDisable={!auth?.shipToCode || preAcceptedTnC}
                    {...restProps}
                />
            ) : null}
            {isIDMarket && accBlocked && <NotificationTileApl accBlocked={accBlocked} handleClose={handleClose} />}
            {shipToAddressListPaginator?.isPopUpVisible ? <ShiptoCodePopUp updateShipToAdd={updateShipToAdd} /> : null}
            <div className={`${contentClassName} ${shouldExpandMaxHeight ? 'flex-grow' : ''}`}>{children}</div>
            {!preAcceptedTnC && (
                <div ref={ref} className={shouldExpandMaxHeight ? 'mt-auto' : ''}>
                    {inView && !isAdminConsole && <Footer />}
                </div>
            )}
            {productWidgetPageTitle ? (
                <ProductWidget widgets={filterWidgetsByPage(productWidgets, productWidgetPageTitle)} />
            ) : null}
        </div>
    );
};

export default Layout;
